import React from 'react';
import { useIntl } from 'react-intl';
import { motion } from 'framer-motion';
import { TrendingUp, DollarSign, Target } from 'lucide-react';
import InfoTooltip from '../common/InfoTooltip';
import { useCurrency } from '../../hooks/useCurrency';

export default function DCFSummaryCards({ sumOfPV = 0, terminalValue = 0, enterpriseValue = 0, className = '' }) {
  const intl = useIntl();
  const { symbol: currencySym } = useCurrency();
  const formatValue = (value) => {
    const v = Number(value || 0);
    if (v >= 1_000_000_000) {
      return `${currencySym}${(v / 1_000_000_000).toFixed(1)}B`;
    }
    if (v >= 1_000_000) {
      return `${currencySym}${(v / 1_000_000).toFixed(1)}M`;
    }
    if (v >= 1_000) {
      return `${currencySym}${(v / 1_000).toFixed(1)}K`;
    }
    return `${currencySym}${Math.round(v).toLocaleString()}`;
  };

  const cards = [
    {
      key: 'sum-dcf',
      title: intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_sum_title' }),
      tooltip: 'The total present value of all projected cash flows during the forecast period (Years 1-5), discounted back to today using the cost of capital.',
      subtitle: intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_sum_subtitle' }),
      value: sumOfPV,
      Icon: DollarSign,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
      borderColor: 'border-blue-500/20',
    },
    {
      key: 'terminal-value',
      title: intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_terminal_title' }),
      tooltip: "The present value of all cash flows expected beyond the forecast period, representing the business's ongoing value assuming it continues operating indefinitely.",
      subtitle: intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_terminal_subtitle' }),
      value: terminalValue,
      Icon: TrendingUp,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
      borderColor: 'border-green-500/20',
    },
    {
      key: 'enterprise-value',
      title: intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_enterprise_title' }),
      tooltip: 'The total enterprise value—the sum of discounted cash flows plus terminal value—representing what the business is worth today based on its future earnings potential.',
      subtitle: intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_enterprise_subtitle' }),
      value: enterpriseValue,
      Icon: Target,
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10',
      borderColor: 'border-purple-500/20',
    },
  ];

  return (
    <div className={`grid grid-cols-1 md:grid-cols-3 gap-6 ${className}`}>
      {cards.map((card, index) => (
        <motion.div
          key={card.key}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: index * 0.1 }}
          className={`bg-card border ${card.borderColor} rounded-lg p-6 hover:shadow-lg transition-shadow`}
        >
          <div className="flex items-start justify-between mb-4">
            <div className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              {card.title}
              <InfoTooltip content={card.tooltip} />
            </div>
            <div className={`${card.bgColor} p-2 rounded-lg`}>
              <card.Icon className={`w-5 h-5 ${card.color}`} />
            </div>
          </div>
          <div className="text-3xl font-bold text-foreground mb-1">{formatValue(card.value)}</div>
          <div className="text-xs text-muted-foreground">
            {card.subtitle}
          </div>
        </motion.div>
      ))}
    </div>
  );
}
